// Reading an Audiotool document into plain data.
//
//   readDocState     → push-reconcile.ts's DocState: every audio/note track
//                      and region, ours or not — the reconciler decides which
//                      it owns, and needs the foreign ones to know a track
//                      still holds someone else's work.
//   readProjection   → projection.ts's AudiotoolProjection, for pulling a
//                      project into Live.
//
// Both walk the document once and hand back data with no live entity handles,
// so the planners stay pure.
//
// Traversal, per the spike: a track's name is on its PLAYER device (tracks
// carry none); its mixer strip is found by following the desktopAudioCable
// from the player's audioOutput to a mixerChannel's audioInput (TR5); an audio
// region's sample length in ticks is the end of its playback automation ramp
// [0 → 0, musicTicks → 1] (IS0–IS7); a note region's notes point at its
// collection.

import type { NexusEntity } from "@audiotool/nexus/document";

import type {
  AudiotoolProjection,
  ProjAudioRegion,
  ProjAudioTrack,
  ProjNoteTrack,
  ProjSample,
  ProjStrip,
  ProjUnmapped,
} from "../../../shared/audiotool/projection.js";
import type { DocState } from "../../../shared/audiotool/push-reconcile.js";
import type { NexusDoc } from "./transact.js";

type Query = NexusDoc["queryEntities"];

export function readDocState(doc: NexusDoc): DocState {
  const q = doc.queryEntities;
  const cfg = q.ofTypes("config").getOne();
  const tracks: DocState["tracks"] = {};
  for (const t of q.ofTypes("audioTrack", "noteTrack").get()) {
    tracks[t.id] = { orderAmongTracks: t.fields.orderAmongTracks.value, regionIds: [] };
  }
  const regions: DocState["regions"] = {};
  for (const r of q.ofTypes("audioRegion", "noteRegion").get()) {
    const trackId = r.fields.track.value.entityId;
    const rf = r.fields.region.fields;
    regions[r.id] = { positionTicks: rf.positionTicks.value, durationTicks: rf.durationTicks.value, trackId };
    tracks[trackId]?.regionIds.push(r.id);
  }
  return {
    config: cfg
      ? {
          tempoBpm: cfg.fields.tempoBpm.value,
          signatureNumerator: cfg.fields.signatureNumerator.value,
          signatureDenominator: cfg.fields.signatureDenominator.value,
          durationTicks: cfg.fields.durationTicks.value,
        }
      : null,
    tracks,
    regions,
  };
}

/** Sample metadata the document doesn't hold (display name, length). */
export type SampleInfo = (sampleName: string) => Promise<{ displayName: string; durationSeconds: number } | null>;

/**
 * Entity types a pull understands and consumes. Anything else in the document
 * is reported — devices by name, the rest as automation — never dropped
 * silently. (Structural glue with no user-visible meaning is ignored.)
 */
const CONSUMED = new Set<string>([
  "config", "groove", "audioTrack", "noteTrack", "audioRegion", "noteRegion", "noteCollection", "note",
  "sample", "automationCollection", "automationEvent", "audioDevice", "mixerChannel", "desktopAudioCable",
  "desktopNoteCable", "desktopPlacement", "mixerMaster", "tempoAutomationTrack",
]);
const AUTOMATION = new Set<string>(["automationTrack", "automationRegion"]);

export async function readProjection(
  doc: NexusDoc,
  meta: { projectId: string; displayName: string },
  sampleInfo: SampleInfo,
): Promise<AudiotoolProjection> {
  const q = doc.queryEntities;
  const cfg = q.ofTypes("config").getOne();
  const playbackEnds = automationEnds(q);

  const audioTracks: ProjAudioTrack[] = [];
  for (const t of q.ofTypes("audioTrack").get()) {
    const player = q.getEntityAs(t.fields.player.value.entityId, "audioDevice");
    const regions: ProjAudioRegion[] = [];
    for (const r of q.ofTypes("audioRegion").get()) {
      if (r.fields.track.value.entityId !== t.id) continue;
      const sampleEntity = q.getEntityAs(r.fields.sample.value.entityId, "sample");
      const name = sampleEntity?.fields.sampleName.value ?? "";
      const info = name ? await sampleInfo(name) : null;
      const ramp = playbackEnds.get(r.fields.playbackAutomationCollection.value.entityId);
      const sample: ProjSample = { name, displayName: info?.displayName ?? name, durationSeconds: info?.durationSeconds ?? 0 };
      regions.push({
        id: r.id,
        ...timing(r),
        sample,
        musicTicks: ramp?.endTicks ?? 0,
        playbackLinear: ramp?.linear ?? false,
        gain: r.fields.gain.value,
        fadeInTicks: r.fields.fadeInDurationTicks.value,
        fadeOutTicks: r.fields.fadeOutDurationTicks.value,
        pitchShiftSemitones: r.fields.pitchShiftSemitones.value,
      });
    }
    audioTracks.push({
      id: t.id,
      displayName: player?.fields.displayName.value ?? "",
      orderAmongTracks: t.fields.orderAmongTracks.value,
      enabled: t.fields.isEnabled.value,
      strip: player ? stripOf(q, player.id) : null,
      processed: player ? processedPath(q, player.id) : false,
      gain: player?.fields.gain.value ?? 1,
      panning: player?.fields.panning.value ?? 0,
      regions,
    });
  }

  const noteTracks: ProjNoteTrack[] = [];
  const notesByCollection = new Map<string, NexusEntity<"note">[]>();
  for (const n of q.ofTypes("note").get()) {
    const c = n.fields.collection.value.entityId;
    const list = notesByCollection.get(c) ?? [];
    list.push(n);
    notesByCollection.set(c, list);
  }
  for (const t of q.ofTypes("noteTrack").get()) {
    const player = q.getEntity(t.fields.player.value.entityId);
    noteTracks.push({
      id: t.id,
      displayName: player ? displayNameOf(player) : "",
      orderAmongTracks: t.fields.orderAmongTracks.value,
      enabled: t.fields.isEnabled.value,
      strip: player ? stripOf(q, player.id) : null,
      instrument: player?.entityType ?? "",
      regions: q.ofTypes("noteRegion").get()
        .filter((r) => r.fields.track.value.entityId === t.id)
        .map((r) => ({
          id: r.id,
          ...timing(r),
          notes: (notesByCollection.get(r.fields.collection.value.entityId) ?? []).map((n) => ({
            pitch: n.fields.pitch.value,
            positionTicks: n.fields.positionTicks.value,
            durationTicks: n.fields.durationTicks.value,
            velocity: n.fields.velocity.value,
            doesSlide: n.fields.doesSlide.value,
          })),
        })),
    });
  }

  const unmappedCounts = new Map<string, ProjUnmapped>();
  for (const e of q.get()) {
    if (CONSUMED.has(e.entityType)) continue;
    const kind: ProjUnmapped["kind"] = AUTOMATION.has(e.entityType) ? "automation" : "device";
    const u = unmappedCounts.get(e.entityType) ?? { kind, entityType: e.entityType, count: 0 };
    u.count++;
    unmappedCounts.set(e.entityType, u);
  }

  return {
    projectId: meta.projectId,
    displayName: meta.displayName,
    config: cfg
      ? {
          tempoBpm: cfg.fields.tempoBpm.value,
          signatureNumerator: cfg.fields.signatureNumerator.value,
          signatureDenominator: cfg.fields.signatureDenominator.value,
        }
      : null,
    tempoAutomation: q.ofTypes("tempoAutomationTrack").get().length > 0,
    audioTracks,
    noteTracks,
    unmapped: [...unmappedCounts.values()].sort((a, b) => a.entityType.localeCompare(b.entityType)),
  };
}

function timing(r: NexusEntity<"audioRegion"> | NexusEntity<"noteRegion">): Omit<ProjAudioRegion, "id" | "sample" | "musicTicks" | "playbackLinear"> {
  const f = r.fields.region.fields;
  return {
    positionTicks: f.positionTicks.value,
    durationTicks: f.durationTicks.value,
    loopOffsetTicks: f.loopOffsetTicks.value,
    loopDurationTicks: f.loopDurationTicks.value,
    collectionOffsetTicks: f.collectionOffsetTicks.value,
    enabled: f.isEnabled.value,
    displayName: f.displayName.value,
  };
}

/**
 * Each playback automation collection's ramp end, and whether it is the plain
 * linear ramp insertSample writes: exactly [0 → 0, end → 1], sloped.
 */
function automationEnds(q: Query): Map<string, { endTicks: number; linear: boolean }> {
  const events = new Map<string, Array<{ at: number; value: number }>>();
  for (const e of q.ofTypes("automationEvent").get()) {
    const c = e.fields.collection.value.entityId;
    const list = events.get(c) ?? [];
    list.push({ at: e.fields.positionTicks.value, value: e.fields.value.value });
    events.set(c, list);
  }
  const out = new Map<string, { endTicks: number; linear: boolean }>();
  for (const [c, list] of events) {
    list.sort((a, b) => a.at - b.at);
    const last = list[list.length - 1]!;
    const first = list[0]!;
    const linear = list.length === 2 && first.at === 0 && first.value === 0 && last.value === 1;
    // A drawn curve: approximate with where playback reaches the sample's end.
    const end = list.find((p) => p.value >= 1)?.at ?? last.at;
    out.set(c, { endTicks: end, linear });
  }
  return out;
}

/**
 * Follow the player's audio along cables to the first mixer channel — through
 * any devices in between (an instrument's effect chain), breadth first, so the
 * nearest channel wins.
 */
function stripOf(q: Query, playerId: string): ProjStrip | null {
  return chainOf(q, playerId).strip;
}

/**
 * The player's path to the mixer: its channel, and every device passed on the
 * way (effects — not the player, not the channel).
 */
function chainOf(q: Query, playerId: string): { strip: ProjStrip | null; stripId: string | null; devices: Set<string> } {
  const cables = q.ofTypes("desktopAudioCable").get();
  const seen = new Set<string>([playerId]);
  const devices = new Set<string>();
  let frontier = [playerId];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const from of frontier) {
      for (const cable of cables) {
        if (cable.fields.fromSocket.value.entityId !== from) continue;
        const to = cable.fields.toSocket.value.entityId;
        const strip = q.getEntityAs(to, "mixerChannel");
        if (strip) {
          const f = strip.fields.faderParameters.fields;
          return {
            strip: {
              id: strip.id,
              preGain: strip.fields.preGain.value,
              gain: f.postGain.value,
              panning: f.panning.value,
              muted: f.isMuted.value,
              soloed: f.isSoloed.value,
            },
            stripId: strip.id,
            devices,
          };
        }
        if (!seen.has(to)) {
          seen.add(to);
          devices.add(to);
          next.push(to);
        }
      }
    }
    frontier = next;
  }
  return { strip: null, stripId: null, devices };
}

/**
 * Whether anything on the player's way to the mixer changes its sound: effect
 * devices in between, the channel's EQ / compressor / trim filter / phase
 * switch, or enabled automation on any of it.
 */
function processedPath(q: Query, playerId: string): boolean {
  const { stripId, devices } = chainOf(q, playerId);
  if (devices.size > 0) return true;
  const strip = stripId ? q.getEntityAs(stripId, "mixerChannel") : undefined;
  if (strip) {
    // A new channel has its EQ on with every band at 0 dB and its trim filter
    // on at 20 Hz–20 kHz: both transparent. What counts is a setting that
    // changes the sound.
    const eq = strip.fields.eq.fields;
    const eqShapes = eq.isActive.value && [eq.lowShelfGainDb, eq.lowMidGainDb, eq.highMidGainDb, eq.highShelfGainDb].some((g) => Math.abs(g.value) > 0.01);
    const trim = strip.fields.trimFilter.fields;
    const trimCuts = trim.isActive.value && (trim.highPassCutoffFrequencyHz.value > 20.5 || trim.lowPassCutoffFrequencyHz.value < 19_999);
    if (eqShapes || trimCuts || strip.fields.compressor.fields.isActive.value || strip.fields.doesPhaseReverse.value) return true;
  }
  const path = new Set<string>([playerId, ...devices, ...(stripId ? [stripId] : [])]);
  return q.ofTypes("automationTrack").get().some(
    (a) => a.fields.isEnabled.value && path.has(a.fields.automatedParameter.value.entityId),
  );
}

/** Most devices carry a displayName; fall back to nothing rather than guess. */
function displayNameOf(e: NexusEntity): string {
  const f = (e.fields as Record<string, unknown>)["displayName"];
  if (f && typeof f === "object" && "value" in f && typeof f.value === "string") return f.value;
  return "";
}
